import { ChangeEvent, useMemo, useState } from 'react';
import {
  Stack,
  Flex,
  Switch,
  Box,
  RadioGroup,
  Radio,
  Alert,
  Text,
  Popover,
  PopoverTrigger,
  LinkButton,
  PopoverContent,
  PopoverBody,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { storeSelector, usePolicyContext } from '@Policy/store';
import {
  ExchangeOnlinePolicyItem,
  RiskAiFilterRuleScope,
} from '@Policy/utils/types';
import { FormLabel } from '@Components/Common/FormComponent';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';
import { useRiskAIRules } from '../hooks/useRiskAIRules';
import { RuleSelection } from '../components/RuleSelection';
import { RuleAggressivitiesActions } from '../components/RuleAggressivitiesActions';
import { RuleExclusionList } from '../components/RuleExclusionList';
import { useRiskAIRulesQuery } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/query';
import { RuleType } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { rulesListLinks } from '../shared';
import { useSocialGraphEnabledQuery } from 'src/pages/GlobalSetting/SocialGraph/query';
import { filterSocialGraphRules } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/utils';
import { AnomalyEmailBannerPreview } from '../components/EmailBannerPreview';

function AnomaliesTab() {
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  const {
    anomalyNotifyType,
    tmRaiFilterRules,
    raiAnomalyRuleScope,
    raiAnomalyAddBannerEnable,
    policyType,
    policyMode,
    setRiskAiState,
  } = contextSelector((s) => {
    const template = s.template as ExchangeOnlinePolicyItem;
    return {
      anomalyNotifyType: template.tmRiskAiFilters.raiAnomalyNotifyType,
      tmRaiFilterRules: template.tmRiskAiFilters.tmRaiFilterRules,
      raiAnomalyRuleScope: template.tmRiskAiFilters.raiAnomalyRuleScope,
      raiAnomalyAddBannerEnable:
        template.tmRiskAiFilters.raiAnomalyAddBannerEnable,
      policyType: template.policyType,
      policyMode: template.policyMode,
      setRiskAiState: s.setRiskAiState,
    };
  });

  const isMonitor =
    policyMode === PolicyModeEnum.DefaultMonitorPolicy ||
    policyMode === PolicyModeEnum.DefaultXDRMonitorPolicy;

  const { data: _rules } = useRiskAIRulesQuery({
    hideSocialGraph: [
      PolicyTypeEnum.Gmail,
      PolicyTypeEnum.ExchangeInline,
      PolicyTypeEnum.GmailInline,
    ].includes(policyType),
  });
  const { data: socialGraphEnabled } = useSocialGraphEnabledQuery();

  const rules = useMemo(() => {
    if (!_rules) return undefined;
    return filterSocialGraphRules(
      _rules ?? [],
      socialGraphEnabled ?? false,
      policyType
    );
  }, [_rules, policyType, socialGraphEnabled]);

  const { preDefinedRules, selectedPreDefinedRules, setSelectedBuiltInRules } =
    useRiskAIRules({
      rules: rules || [],
      policyRules: tmRaiFilterRules,
      onRulesUpdate: (rules) => setRiskAiState({ tmRaiFilterRules: rules }),
    });

  const defaultRulesSelection = (
    <Stack gap="6x" ml="6x">
      <RuleAggressivitiesActions preDefinedRules={preDefinedRules} />
      <RuleExclusionList rules={rules || []} />
    </Stack>
  );

  const specifiedRulesSelection = (
    <Box ml="6x">
      <RuleSelection
        allRules={preDefinedRules}
        selectedRules={selectedPreDefinedRules}
        description={t(
          'policy.riskAi.anomalies.availableRules.preDefined.desc'
        )}
        onSelectionChange={setSelectedBuiltInRules}
        rulesListLink={rulesListLinks[RuleType.PreDefined]}
      />
    </Box>
  );

  return (
    <Stack gap="6x" p="4x">
      <Stack gap="4x">
        <Alert variant="outline" severity="info">
          <Text>
            {[PolicyTypeEnum.Gmail].includes(policyType)
              ? t('policy.riskAi.anomalies.gmail.desc')
              : t('policy.riskAi.anomalies.desc')}
          </Text>
        </Alert>
        <RadioGroup
          value={`${raiAnomalyRuleScope}`}
          onChange={(scope) =>
            setRiskAiState({ raiAnomalyRuleScope: Number(scope) })
          }
        >
          <Stack gap="4x" direction="row" shouldWrapChildren>
            <Radio value={`${RiskAiFilterRuleScope.AllRules}`}>
              {t('policy.riskAi.allPredefinedRules')}
            </Radio>
            <Radio value={`${RiskAiFilterRuleScope.SpecificRules}`}>
              {t('policy.riskAi.specifiedPredefinedRules')}
            </Radio>
          </Stack>
        </RadioGroup>
        <Stack gap="4x">
          {raiAnomalyRuleScope === RiskAiFilterRuleScope.AllRules &&
            defaultRulesSelection}
          {raiAnomalyRuleScope === RiskAiFilterRuleScope.SpecificRules &&
            specifiedRulesSelection}
          <Flex alignItems="center">
            <FormLabel label={t('policy.label.notification')} />
            <Switch
              value={anomalyNotifyType}
              checked={!!anomalyNotifyType}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setRiskAiState({
                  raiAnomalyNotifyType: Number(e.target.checked),
                });
              }}
            >
              {anomalyNotifyType === 0
                ? t('policy.status.off')
                : t('policy.status.on')}
            </Switch>
          </Flex>
        </Stack>
      </Stack>
      {policyType === PolicyTypeEnum.Exchange && (
        <Flex alignItems="center">
          <FormLabel label={t('policy.label.emailBanner')} />
          <Switch
            value={raiAnomalyAddBannerEnable}
            checked={isMonitor ? false : !!raiAnomalyAddBannerEnable}
            disabled={isMonitor}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setRiskAiState({
                raiAnomalyAddBannerEnable: Number(e.target.checked),
              });
            }}
          >
            {raiAnomalyAddBannerEnable === 0
              ? t('policy.status.off')
              : t('policy.status.on')}
          </Switch>
          <Popover
            placement="right-start"
            isOpen={emailPreviewOpen}
            onClose={() => setEmailPreviewOpen(false)}
          >
            <PopoverTrigger>
              <LinkButton
                ml="4x"
                disabled={isMonitor}
                onClick={() => setEmailPreviewOpen((prev) => !prev)}
              >
                {t('common.label.preview')}
              </LinkButton>
            </PopoverTrigger>
            <PopoverContent padding={0}>
              <PopoverBody>
                <AnomalyEmailBannerPreview
                  onClose={() => setEmailPreviewOpen(false)}
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Flex>
      )}
    </Stack>
  );
}

export default AnomaliesTab;
